import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/storage';
import {
  buildPurchaseConfirmation,
  CHECKOUT_IN_PROGRESS_KEY,
  formatCadence,
  PURCHASE_CONFIRMATION_KEY,
} from '../utils/subscriptionPurchase';

const stripePromiseCache = new Map();

const getStripeInstance = (publishableKey) => {
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromiseCache.has(publishableKey)) {
    stripePromiseCache.set(
      publishableKey,
      import('@stripe/stripe-js/pure').then(({ loadStripe }) => loadStripe(publishableKey))
    );
  }
  return stripePromiseCache.get(publishableKey);
};

const SubscriptionCheckout = ({ onUpdated = null }) => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [planSummary, setPlanSummary] = useState(null);
  const [showAnnualUpsellConfirm, setShowAnnualUpsellConfirm] = useState(false);
  const mountRef = useRef(null);
  const embeddedInstanceRef = useRef(null);
  const checkoutStartedRef = useRef(false);
  const completionStartedRef = useRef(false);

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const planCode = search.get('plan_code') || '';
  const billingCadence = search.get('billing_cadence') || '';
  const returnSessionId = search.get('session_id') || '';

  const clearCheckoutInProgress = () => {
    removeStoredItem(CHECKOUT_IN_PROGRESS_KEY);
  };

  const readCheckoutInProgress = () => {
    try {
      const stored = getStoredItem(CHECKOUT_IN_PROGRESS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      clearCheckoutInProgress();
      return null;
    }
  };

  const hasActivePaidSubscription = (subscription) => ['active', 'scheduled_cancel', 'scheduled_downgrade']
    .includes(subscription?.raw_status || subscription?.status);

  const markCheckoutInProgress = (nextPlanCode, nextBillingCadence) => {
    setStoredItem(CHECKOUT_IN_PROGRESS_KEY, JSON.stringify({
      plan_code: nextPlanCode,
      billing_cadence: nextBillingCadence,
      started_at: Date.now(),
    }));
  };

  const restoreCompletedCheckout = async (subscriptionResponse) => {
    const nextData = subscriptionResponse?.data || null;
    const confirmation = nextData?.subscription ? buildPurchaseConfirmation({
      subscription: nextData.subscription,
      type: 'activated',
    }) : null;
    setStoredItem(PURCHASE_CONFIRMATION_KEY, JSON.stringify({
      org_id: nextData?.org_id || null,
      notice: 'Subscription activated successfully.',
      confirmation,
    }));
    clearCheckoutInProgress();
    if (onUpdated) await onUpdated();
    history.replace('/account/subscription');
  };

  const goBackToSubscription = () => {
    clearCheckoutInProgress();
    history.replace('/account/subscription');
  };

  const handleSwitchToAnnual = () => {
    if (embeddedInstanceRef.current?.destroy) {
      embeddedInstanceRef.current.destroy();
    }
    embeddedInstanceRef.current = null;
    checkoutStartedRef.current = false;
    completionStartedRef.current = false;
    setError('');
    setStatusMessage('');
    markCheckoutInProgress(planCode, 'annual');
    history.replace(`/account/subscription/checkout?plan_code=${encodeURIComponent(planCode)}&billing_cadence=annual`);
  };

  useEffect(() => () => {
    if (embeddedInstanceRef.current?.destroy) {
      embeddedInstanceRef.current.destroy();
    }
  }, []);

  useEffect(() => {
    checkoutStartedRef.current = false;
    completionStartedRef.current = false;
    setPlanSummary(null);
    if (embeddedInstanceRef.current?.destroy) {
      embeddedInstanceRef.current.destroy();
    }
    embeddedInstanceRef.current = null;
  }, [planCode, billingCadence]);

  useEffect(() => {
    let cancelled = false;

    const finalizeCheckout = async (sessionId) => {
      if (!sessionId || completionStartedRef.current) return;
      completionStartedRef.current = true;
      setLoading(true);
      setError('');
      setStatusMessage('Finalizing your subscription...');
      try {
        await api.post('/acct-mgmt/v1/account/subscription/complete', { session_id: sessionId });
        const subscriptionResponse = await api.get('/acct-mgmt/v1/account/subscription');
        const nextData = subscriptionResponse.data;
        const confirmation = nextData?.subscription ? buildPurchaseConfirmation({
          subscription: nextData.subscription,
          type: 'activated',
        }) : null;
        setStoredItem(PURCHASE_CONFIRMATION_KEY, JSON.stringify({
          org_id: nextData?.org_id || null,
          notice: 'Subscription activated successfully.',
          confirmation,
        }));
        clearCheckoutInProgress();
        if (onUpdated) await onUpdated();
        if (!cancelled) {
          history.replace('/account/subscription');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to finalize subscription checkout.');
          setStatusMessage('');
          setLoading(false);
          completionStartedRef.current = false;
        }
      }
    };

    const startEmbeddedCheckout = async () => {
      if (checkoutStartedRef.current) return;
      if (!planCode || !billingCadence) {
        setError('Plan code and billing cadence are required to start checkout.');
        setLoading(false);
        return;
      }
      checkoutStartedRef.current = true;
      setLoading(true);
      setError('');
      setStatusMessage('Preparing secure checkout...');
      try {
        const subscriptionResponse = await api.get('/acct-mgmt/v1/account/subscription');
        const existingSubscription = subscriptionResponse.data?.subscription || null;
        if (hasActivePaidSubscription(existingSubscription)) {
          const checkoutInProgress = readCheckoutInProgress();
          const matchesCurrentAttempt = checkoutInProgress
            && checkoutInProgress.plan_code === planCode
            && checkoutInProgress.billing_cadence === billingCadence;
          const isRecentAttempt = Number(checkoutInProgress?.started_at || 0) > (Date.now() - (60 * 60 * 1000));
          if (matchesCurrentAttempt && isRecentAttempt) {
            await restoreCompletedCheckout(subscriptionResponse);
            return;
          }
          clearCheckoutInProgress();
          history.replace('/account/subscription');
          return;
        }
        const checkoutResponse = await api.post('/acct-mgmt/v1/account/subscription/checkout', {
          plan_code: planCode,
          billing_cadence: billingCadence,
        });
        const availablePlans = subscriptionResponse.data?.available_plans || [];
        const matchingPlan = availablePlans.find((plan) => plan.plan_code === planCode) || null;
        if (matchingPlan && !cancelled) {
          setPlanSummary({
            planName: matchingPlan.plan_name,
            annualDiscountPct: Number(matchingPlan.annual_discount_pct || 0),
          });
        }
        const checkoutData = checkoutResponse.data || {};
        if (!checkoutData.client_secret || !checkoutData.publishable_key || !checkoutData.session_id) {
          throw new Error('Checkout session data was not returned.');
        }
        const stripe = await getStripeInstance(checkoutData.publishable_key);
        if (!stripe) {
          throw new Error('Payment checkout failed to initialize.');
        }
        if (embeddedInstanceRef.current?.destroy) {
          embeddedInstanceRef.current.destroy();
        }
        const embeddedCheckoutPage = await stripe.createEmbeddedCheckoutPage({
          clientSecret: checkoutData.client_secret,
          onComplete: async () => {
            await finalizeCheckout(checkoutData.session_id);
          },
        });
        embeddedInstanceRef.current = embeddedCheckoutPage;
        if (!cancelled && mountRef.current) {
          embeddedCheckoutPage.mount(mountRef.current);
          setStatusMessage('');
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to start embedded checkout.');
          setStatusMessage('');
          setLoading(false);
          checkoutStartedRef.current = false;
        }
      }
    };

    if (returnSessionId) {
      finalizeCheckout(returnSessionId);
    } else {
      startEmbeddedCheckout();
    }

    return () => {
      cancelled = true;
    };
  }, [billingCadence, history, onUpdated, planCode, returnSessionId]);

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-xl-10">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">Secure Checkout</h4>
            </div>
            <Link to="/account/subscription" className="btn btn-outline-danger btn-sm">
              Cancel Checkout
            </Link>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start gap-3 pb-3 mb-3 border-bottom">
                <h5 className="mb-0">Order Summary</h5>
                {billingCadence === 'monthly' && Number(planSummary?.annualDiscountPct || 0) > 0 && (
                  <button type="button" className="btn btn-outline-success btn-sm" onClick={() => setShowAnnualUpsellConfirm(true)}>
                    <i className="fas fa-tags me-1" aria-hidden="true"></i>
                    {`Save ${planSummary.annualDiscountPct}% with Annual`}
                  </button>
                )}
              </div>
              <div className="small mb-2"><strong>Plan:</strong> {planCode ? `${planCode.charAt(0).toUpperCase()}${planCode.slice(1)}` : '—'}</div>
              <div className="small mb-2">
                <strong>Billing Frequency:</strong> {formatCadence(billingCadence)}
                {billingCadence === 'monthly' ? ' (first month charges prorated through end of calendar month)' : ''}
              </div>
              <div className="small mb-2">
                <strong>Billing Policy:</strong> Charges are non-refundable after purchase.
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <div>{error}</div>
                  <div className="mt-3">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBackToSubscription}>
                      Return to Subscription
                    </button>
                  </div>
                </div>
              )}
              {!error && statusMessage && (
                <div className="text-muted mb-3">{statusMessage}</div>
              )}
              <div ref={mountRef} />
              {!error && loading && !statusMessage && (
                <div className="text-muted">Loading checkout...</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showAnnualUpsellConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Switch to Annual Billing?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowAnnualUpsellConfirm(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    This will restart checkout with annual billing and discard any information entered in the current checkout form.
                  </p>
                  <p className="mb-0">
                    {`You will save ${Number(planSummary?.annualDiscountPct || 0)}% by switching to annual.`}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowAnnualUpsellConfirm(false)}
                  >
                    Keep Monthly
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      setShowAnnualUpsellConfirm(false);
                      handleSwitchToAnnual();
                    }}
                  >
                    Switch to Annual
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionCheckout;
